<template>
  <base-card :size="size"
             icon="icon-arrow-down-bold"
             @click="openAvailabilityModal">
    <div slot="title">
      {{ $t("deviceAvailability.title") }}
    </div>

    <div slot="content"
         class="device-availability-control"
         :class="classes"
         v-if="devices && devices.length">
      <div v-if="scheduleCreated">
        <div v-if="!screentimeExhausted" class="device-availability-control-body">
          <div v-if="currentRule.reason === 'schedule'" class="mode">
            <div class="mode-reason">
              {{
                mode.name === 'Standard'
                  ? $t("deviceAvailability.internetWithProfileSettings")
                  : mode.name === 'Pause Device'
                    ? $t("deviceAvailability.devicePaused")
                    : mode.name
              }}
            </div>

            <div v-if="mode.name === 'Pause Device'" class="mode-reason">
              {{ $t("deviceAvailability.iOSLimitedToNoInternet") }}
            </div>

            <div v-if="scheduledAllDay" class="mode-name">
              {{ $t("deviceAvailability.scheduledAllDay") }}
            </div>

            <div v-else class="mode-name">
              {{ $t("deviceAvailability.scheduled") }} {{ `${modeDuration.start} - ${modeDuration.end}` }}
            </div>
          </div>

          <div v-if="currentRule.reason === 'parental_override'" class="mode">
            <div class="mode-reason">
              {{
                mode.name === 'Standard'
                  ? $t("deviceAvailability.internetWithProfileSettings")
                  : mode.name === 'Pause Device'
                    ? $t("deviceAvailability.devicePaused")
                    : mode.name
              }}
            </div>

            <div v-if="mode.name === 'Pause Device'" class="mode-reason">
              {{ $t("deviceAvailability.iOSLimitedToNoInternet") }}
            </div>

            <div class="mode-name">
              {{ $t("deviceAvailability.scheduleNotRunning") }}
            </div>
          </div>
        </div>

        <div v-else class="device-availability-control-body">
          <div class="mode">
            <div class="mode-reason">
              {{ $t("deviceAvailability.screenTimeExhausted") }}
            </div>

            <div class="mode-name">
              {{
                mode.name === 'Pause Device'
                  ? $t("deviceAvailability.devicePaused")
                  : $t("deviceAvailability.noInternet")
              }}
            </div>

            <div v-if="mode.name === 'Pause Device'" class="mode-name">
              {{ $t("deviceAvailability.iOSLimitedToNoInternet") }}
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <div v-if="!screentimeExhausted" class="device-availability-control-body">
          <div class="mode-name">
            {{
              mode.name === 'Standard'
                ? $t("deviceAvailability.internetWithProfileSettings")
                : mode.name === 'Pause Device'
                  ? $t("deviceAvailability.devicePaused")
                  : mode.name
            }}
          </div>

          <div v-if="mode.name === 'Pause Device'" class="mode-name">
            {{ $t("deviceAvailability.iOSLimitedToNoInternet") }}
          </div>
        </div>

        <div v-else class="device-availability-control-body">
          <div class="mode-reason">
            {{ $t("deviceAvailability.screenTimeExhausted") }}
          </div>

            <div class="mode-name">
              {{
                mode.name === 'Pause Device'
                  ? $t("deviceAvailability.devicePaused")
                  : $t("deviceAvailability.noInternet")
              }}
            </div>

            <div v-if="mode.name === 'Pause Device'" class="mode-name">
              {{ $t("deviceAvailability.iOSLimitedToNoInternet") }}
            </div>
        </div>
      </div>
    </div>

    <div v-else slot="content" class="no-device">
      {{ $t("noDevices") }}
    </div>
  </base-card>
</template>

<script>
  import * as intl from '@/services/intl'

  export default {
    props: {
      childId: {
        required: true,
        type: String
      },

      size: {
        type: String,
        default: 'big'
      }
    },

    computed: {
      ...mapGetters('children', {
        getChildById: 'getById'
      }),

      child() {
        return this.getChildById(this.childId)
      },

      screentimeExhausted() {
        if (!this.child || !this.child.latest_accumulated_screentime) {
          return false
        }

        let usedScreenTime = Math.floor(this.child.latest_accumulated_screentime.sum_in_seconds / 60)

        return usedScreenTime >= this.screenTimeLimit
      },

      screenTimeLimit() {
        if (intl.isToday(this.child.settings.screen_time_override_date)) {
          return this.child.settings.screen_time_override_in_minutes
        }

        return this.child.settings.screen_time_limit_in_minutes[intl.getDayOfTheWeek()]
      },

      mode() {
        return this.child.modes.find(mode => mode.id === this.child.current_rule.mode_id)
      },

      currentRule() {
        return this.child.current_rule
      },

      devices() {
        return this.child ? this.child.devices : []
      },

      classes() {
        return {
          'device-availability-control-mobile': this.size === 'mobile'
        }
      },

      modeDuration() {
        const [hours, minutes, seconds] = this.child.current_rule.start_time.split(':')

        const start_time = new Date()
        start_time.setHours(hours, minutes, seconds, 0)

        const end_time = new Date(start_time.getTime() + this.child.current_rule.duration_in_minutes * 60000)

        return {
          start: intl.localTime(start_time),
          end: intl.localTime(end_time)
        }
      },

      scheduledAllDay() {
        return (
          this.modeDuration.start === intl.startOfDay({ local: true }) &&
          this.modeDuration.end === intl.startOfDay({ local: true })
        )
      },

      scheduleCreated() {
        return this.child.time_blocks.length
      },

      ...mapGetters('auth', {
        currentUser: 'currentUser'
      })
    },

    methods: {
      openAvailabilityModal() {
        if (!this.currentUser.hasPaidLicense || !this.devices.length) {
          return
        }

        this.showModal('deviceAvailability', {
          childId: this.child.id,
          screentimeExhausted: this.screentimeExhausted
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .device-availability-control {
    display: flex;
    align-items: center;
    justify-content: space-between;

    height: 75px;

    &-body {
      .mode {
        &-name {
          color: map-get($colors, primary);

          font-style: normal;
          font-weight: bold;
          font-size: 18px;
          line-height: 24px;
        }

        &-reason {
          color: map-get($colors, disabled);

          font-style: normal;
          font-weight: bold;
          font-size: 12px;
          line-height: 14px;
          letter-spacing: 1.1px;
          text-transform: uppercase;
        }
      }
    }

    .disabled {
      opacity: .5;
    }
  }

  .device-availability-control-mobile {
    .mode {
      .mode-name {
        font-size: 16px;
      }

      &-reason {
        font-size: 10px;
      }
    }
  }

  .no-device {
    display: flex;
    align-items: center;

    height: 75px;
    font-size: 18px;
    font-weight: bold;
    color: map-get($colors, disabled);
  }
</style>
